import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { studentAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

// The backend returns snake_case; the StudentProfile page reads camelCase.
// Normalize a single student object so the profile view works.
const toCamel = (obj) => {
  if (Array.isArray(obj)) return obj.map(toCamel);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      const ck = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      out[ck] = toCamel(v);
    }
    return out;
  }
  return obj;
};

const normalizeStudent = (s) => {
  if (!s) return s;
  const c = toCamel(s);
  if (c.streamName && !c.stream) c.stream = { name: c.streamName, id: c.streamId };
  if (c.admissionNumber && !c.studentId) c.studentId = c.admissionNumber;
  return c;
};

// Many endpoints return the object directly (not wrapped in { data }); some wrap it.
const unwrap = (response) => response.data?.data ?? response.data;

export const fetchStudents = createAsyncThunk('students/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await studentAPI.fetchStudents(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch students'));
  }
});

export const fetchStudent = createAsyncThunk('students/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await studentAPI.fetchStudent(id);
    return unwrap(response);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch student'));
  }
});

export const createStudent = createAsyncThunk('students/create', async (data, { rejectWithValue }) => {
  try {
    const response = await studentAPI.createStudent(data);
    return unwrap(response);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create student'));
  }
});

export const updateStudent = createAsyncThunk('students/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await studentAPI.updateStudent(id, data);
    return unwrap(response);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update student'));
  }
});

export const deleteStudent = createAsyncThunk('students/delete', async (id, { rejectWithValue }) => {
  try {
    await studentAPI.deleteStudent(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete student'));
  }
});

export const promoteStudent = createAsyncThunk('students/promote', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await studentAPI.promoteStudent(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to promote student'));
  }
});

export const transferStudent = createAsyncThunk('students/transfer', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await studentAPI.transferStudent(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to transfer student'));
  }
});

export const uploadDocument = createAsyncThunk('students/uploadDocument', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await studentAPI.uploadDocument(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to upload document'));
  }
});

const initialState = {
  students: [],
  currentStudent: null,
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
};

const studentSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    clearStudentError: (state) => {
      state.error = null;
    },
    setCurrentStudent: (state, action) => {
      state.currentStudent = action.payload;
    },
    clearCurrentStudent: (state) => {
      state.currentStudent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload.data || action.payload;
        state.total = action.payload.total || 0;
        state.page = action.payload.page || 1;
        state.limit = action.payload.limit || 10;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStudent.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStudent = normalizeStudent(action.payload);
      })
      .addCase(fetchStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createStudent.pending, (state) => {
        state.loading = true;
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.students.unshift(action.payload);
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateStudent.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.students = state.students.map((s) =>
          s.id === action.payload.id ? action.payload : s
        );
        state.currentStudent = normalizeStudent(action.payload);
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.students = state.students.filter((s) => s.id !== action.payload);
        if (state.currentStudent?.id === action.payload) {
          state.currentStudent = null;
        }
      })
      .addCase(promoteStudent.fulfilled, (state, action) => {
        state.students = state.students.map((s) =>
          s.id === action.payload.id ? action.payload : s
        );
        state.currentStudent = normalizeStudent(action.payload);
      })
      .addCase(transferStudent.fulfilled, (state, action) => {
        state.students = state.students.map((s) =>
          s.id === action.payload.id ? action.payload : s
        );
        state.currentStudent = normalizeStudent(action.payload);
      });
  },
});

export const { clearStudentError, setCurrentStudent, clearCurrentStudent } = studentSlice.actions;
export default studentSlice.reducer;
